import { Component, computed, effect, inject, input, signal, untracked } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EventFormStore } from "../event.form.store";
import { EventFormOccurrenceUtil } from "../../../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { Item } from "../../../pojo/Item";
import { S25Array } from "../../../util/core/s25.array";
import { S25Number } from "../../../util/core/s25.number";
import { EventFormObjectDetails } from "./object.details.component";
import { S25LoadingInlineStaticComponent } from "../../../modules/s25-loading/s25.loading.inline.static.component";
import type { EventForm } from "..";

const PAGE_SIZE = 50;

/** One or more occurrences to re-check instead of the whole event. */
export type EventFormSpecificOccurrences = EventForm.Occurrence | EventForm.Occurrence[];

/** Payload of the form's `refresh` broadcast to the details lists. */
export interface EventFormObjectDetailsRefreshData {
    autoRecreateDetailsList?: boolean;
    specificOcc?: EventFormSpecificOccurrences;
}

type DetailsListLoadOptions = {
    /** Limits the availability check to these occurrences instead of the whole event. */
    specificOcc?: EventFormSpecificOccurrences;
    /** Hides the rows until the pass settles, as `autoRecreateDetailsList` asks for. */
    hideRows?: boolean;
    /** Waits on the availability already in flight rather than starting another pass. */
    waitOnly?: boolean;
    /** First paint: locations already on a saved event skip the availability call. */
    isInitial?: boolean;
};

@TypeManagerDecorator("s25-event-form-object-details-list")
@Component({
    selector: "s25-event-form-object-details-list",
    standalone: true,
    imports: [EventFormObjectDetails, S25LoadingInlineStaticComponent],
    host: { "[attr.data-item-type-id]": "itemTypeId()" },
    template: `
        @if (ready()) {
            @for (item of visibleItems(); track item.itemId) {
                <div
                    class="rose--added-object__wrapper"
                    [class.rose--added-object__wrapper-wide]="isSingleOccurrence()"
                >
                    @if (item.checkedObj) {
                        <div
                            class="rose--added-object c-margin-bottom--half"
                            [class.rose--added-object__location]="isLocationList()"
                            [class.rose--added-object__resource]="!isLocationList()"
                        >
                            <s25-event-form-object-details [item]="item" />
                        </div>
                    } @else {
                        <s25-ng-loading-inline-static [text]="'Loading...'" />
                    }
                </div>
            }

            @if (hasMorePages() && !loadingPage()) {
                <div>
                    <button class="aw-button aw-button--outline" (click)="nextPage()">Load More</button>
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: block;
        }
        .rose--added-object {
            background-color: var(--color--surface-bg);
            border-radius: var(--border-radius);
            border: 1px solid var(--color--border);
            padding: 0.625rem 0.3125rem 0.625rem 0.5rem;
            position: relative;
        }
        .rose--added-object {
            &:before {
                inset-block: -1px;
                inset-inline-start: -1px;
                border-inline-start: var(--border-radius) solid var(--object--color, transparent);
                border-top-left-radius: var(--border-radius);
                border-bottom-left-radius: var(--border-radius);
                content: "";
                position: absolute;
            }
        }
        .rose--added-object__location {
            --object--color: var(--color--objects-location);
        }
        .rose--added-object__resource {
            --object--color: var(--color--objects-resource);
        }
    `,
})
export class EventFormObjectDetailsList {
    readonly itemTypeId = input.required({
        transform: (value: Item.Ids | number | string) => S25Number.parseInt(value),
    });
    /**
     * don't use this directly, because it can be mutated in place.
     * instead, use {@link items}
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    readonly itemsInput = input<EventForm.ObjectItem[]>([], { alias: "items" });

    private readonly eventForm = inject(EventFormStore);

    /** The form model mutates `locations` / `resources` in place, so this re-reads on every revision */
    readonly items = computed(
        (): EventForm.ObjectItem[] => {
            this.eventForm.revision();
            return this.itemsInput() ?? [];
        },
        { equal: () => false },
    );

    readonly loadedPagesCount = signal(1);
    readonly loadingPage = signal(false);
    readonly ready = signal(false);

    /** Last item count the reveal check saw; only the effect reads or writes it. */
    private previousItemCount: number | undefined;

    readonly isLocationList = computed(() => this.itemTypeId() === Item.Ids.Location);
    readonly visibleCount = computed(() => this.loadedPagesCount() * PAGE_SIZE);
    readonly visibleItems = computed(() => this.items().slice(0, this.visibleCount()));
    readonly hasMorePages = computed(() => this.visibleCount() < this.items().length);
    readonly isSingleOccurrence = computed(() => (this.eventForm.model().occurrences?.length ?? 0) === 1);

    constructor() {
        effect((onCleanup) => {
            const itemTypeId = this.itemTypeId();
            if (!itemTypeId) return;
            this.eventForm.registerDetailsList(itemTypeId, this);
            onCleanup(() => this.eventForm.unregisterDetailsList(itemTypeId, this));
        });

        const initialLoadEffect = effect(() => {
            if (!this.itemTypeId()) return;
            untracked((): void => void this.load({ isInitial: true }));
            initialLoadEffect.destroy();
        });

        effect(() => this.checkRevealedRowAvailability(this.items().length));
    }

    refresh(data?: EventFormObjectDetailsRefreshData): void {
        void this.load({ hideRows: data?.autoRecreateDetailsList, specificOcc: data?.specificOcc });
    }

    recreate(): void {
        void this.load({ hideRows: true, waitOnly: true });
    }

    checkAvailForItems(requestedItems: { itemId: number }[]): void {
        const requestedIds = new Set(requestedItems.map((requested) => requested.itemId));
        const items = this.items().filter((item) => requestedIds.has(item.itemId));
        if (items.length) void this.checkAvailability(items);
    }

    async nextPage(): Promise<void> {
        if (this.loadingPage()) return;
        const nextPageItems = this.items().slice(this.visibleCount(), this.visibleCount() + PAGE_SIZE);
        try {
            this.loadingPage.set(true);
            await this.checkAvailability(nextPageItems);
            this.loadedPagesCount.update((count) => count + 1);
        } catch {
        } finally {
            this.loadingPage.set(false);
        }
    }

    async checkAvailability(items: EventForm.ObjectItem[], specificOccs?: EventFormSpecificOccurrences): Promise<void> {
        const formModel = this.eventForm.model();
        const occurrences = specificOccs ? S25Array.forceArray(specificOccs) : undefined;
        const promise = occurrences
            ? EventFormOccurrenceUtil.checkAvailability(formModel, items, undefined, occurrences)
            : EventFormOccurrenceUtil.checkAvailability(formModel, items);
        formModel.availabilityChecksInFlight = (formModel.availabilityChecksInFlight ?? 0) + 1;
        formModel.checkQuotas?.(formModel);
        (formModel.availabilityPromises ??= []).push(promise);
        try {
            await promise;
            formModel.syncItemsCheckedObjWithListFn?.(items)?.();
            this.eventForm.bump();
        } finally {
            formModel.availabilityChecksInFlight = (formModel.availabilityChecksInFlight ?? 0) - 1;
        }
    }

    /** Waits on every availability check the form has started, including other lists' checks. */
    settleAvailability(): Promise<unknown> {
        return Promise.all(this.eventForm.model().availabilityPromises ?? []);
    }

    private async load(options: DetailsListLoadOptions = {}): Promise<void> {
        const itemTypeId = this.itemTypeId();
        const formModel = this.eventForm.model();
        if (!itemTypeId) return;

        if (options.hideRows) this.ready.set(false);

        if (!options.waitOnly) {
            if (options.isInitial && itemTypeId === Item.Ids.Location && formModel.skipLocationInitialAvailCheck) {
                // Locations already saved on the event cannot conflict with themselves, so the first
                // pass marks them clear instead of paying for a check.
                this.items().forEach((item) => (item.checkedObj = { conflicts: [] }));
                this.eventForm.bump();
            } else {
                await this.checkAvailability(this.visibleItems(), options.specificOcc);
            }
        }

        await this.settleAvailability();
        this.ready.set(true);
    }

    /**
     * Any removal — the Remove button, a rule, a failed hold — splices the form model and bumps the
     * revision. When the list was paginated, that pulls a hidden row into view with no availability yet.
     */
    private checkRevealedRowAvailability(itemCount: number): void {
        const previousCount = this.previousItemCount;
        this.previousItemCount = itemCount;
        if (previousCount === undefined || itemCount >= previousCount) return;

        untracked(() => {
            if (previousCount <= this.visibleCount()) return;
            const revealed = this.visibleItems()[this.visibleCount() - 1];
            if (revealed) void this.checkAvailability([revealed]);
        });
    }
}
